import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { CommonModule, CurrencyPipe } from '@angular/common';

import { DigitsGroupingPipe } from '../pipes';
import { LightCarouselComponent } from '@light-carousel';
import { ProductCart, calculateProductPoints } from '@models';
import { AlertMessageComponent } from '..';
import {
  ProcessedProduct,
  EngravingUtilitiesService,
  EngravingModalService,
  type DisabledEngravingModalData,
  UserService,
  ProductProcessingService,
} from '@core';
import { CartOldPriceComponent } from '../cart-old-price/cart-old-price.component';

@Component({
    selector: 'lib-cart-summary-item',
    templateUrl: './cart-summary-item.component.html',
    styleUrl: './cart-summary-item.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        CurrencyPipe,
        LightCarouselComponent,
        DigitsGroupingPipe,
        AlertMessageComponent,
        CartOldPriceComponent,
    ]
})
export class CartSummaryItemComponent {
  product = input<ProductCart | ProcessedProduct>();

  userService = inject(UserService);
  private engravingService = inject(EngravingUtilitiesService);
  private processingService = inject(ProductProcessingService);
  private engravingModalService = inject(EngravingModalService);

  vm = computed(() => {
    const product = this.product();
    const price = product?.price ?? 0;
    const discountedPrice = product?.discountedPrice ?? 0;
    const pointConversionRate = product?.pointConversionRate;

    const pricePoints = calculateProductPoints(price, pointConversionRate, this.userService.conversionRate());
    const discountedPricePoints = discountedPrice
      ? calculateProductPoints(discountedPrice, pointConversionRate, this.userService.conversionRate())
      : { newPoints: 0, oldPoints: null, hasSpecialRate: false };

    // Determine what to show for old/new points
    const hasSpecialRate = pricePoints.hasSpecialRate;
    const isPromo = product?.isPromo;

    let displayNewPoints: number;
    let displayOldPoints: number | null;
    let displayNewPrice: number;
    let displayOldPrice: number | null;

    if (isPromo && discountedPrice) {
      // Price discount: show discounted price/points, old regular price/points
      displayNewPoints = discountedPricePoints.newPoints;
      displayNewPrice = discountedPrice;
      displayOldPrice = price;
      // Old points could be at special rate or standard rate
      displayOldPoints = hasSpecialRate ? pricePoints.oldPoints : pricePoints.newPoints;
    } else {
      // No price discount
      displayNewPoints = pricePoints.newPoints;
      displayNewPrice = price;
      displayOldPrice = null;
      // If special rate, show standard rate points as old
      displayOldPoints = hasSpecialRate ? pricePoints.oldPoints : null;
    }

    return {
      product: product,
      smallImagesForCards: product?.images,
      price: price,
      points: pricePoints.newPoints,
      discountedPrice: discountedPrice,
      discountedPoints: discountedPricePoints.newPoints,
      // New display values
      displayNewPoints,
      displayOldPoints,
      displayNewPrice,
      displayOldPrice,
      hasSpecialRate,
      shouldShowOldPoints: displayOldPoints !== null,
    };
  });

  hasEngraving(product: ProductCart | ProcessedProduct): boolean {
    if (this.isProcessedProduct(product)) {
      return this.engravingService.hasEngraving(product);
    }
    return false;
  }
  isProcessedProduct(product: ProductCart | ProcessedProduct): product is ProcessedProduct {
    return 'engravingText' in product && 'isEngravable' in product;
  }
  viewEngravingDetails(product: ProductCart | ProcessedProduct): void {
    if (!this.isProcessedProduct(product)) {
      console.warn('Cannot view engraving details for unprocessed product:', product.productId);
      return;
    }
    if (!product.engravingText) {
      console.warn('No engraving text found for product:', product.productId);
      return;
    }

    const disabledModalData: DisabledEngravingModalData = {
      product: product,
      engravingText: product.engravingText,
    };

    this.engravingModalService.openDisabledModal(disabledModalData);
  }
}
